import { LineArrowIcon } from '@/src/assets/icons'
import { Box, Typography } from '@mui/material'
import React from 'react'
import { DataType } from '../utils'
import { useStore } from '@/src/types/store'
import { BooleanSchema } from 'yup'
import { formatDecimalValue, formatNumberInMillion } from '@/src/common'

interface TableProps {
  marketData: DataType[]
  currentStatsData: any
  newGraph?: boolean
  newMarketData?: StockData
}

interface StockData {
  current_price: number
  id: number
  bid: number
  dividend_yield: number
  open_price: number
  ask: number
  last_updated: string
  earning_per_share: number
  created_on: string
  high: number
  shares_outstanding: number
  updated_on: string
  low: number
  turnover: number
  previous_close: number
  vwap: number
  market_cap: number
  _52w_high: number
  pe_ratio: number
  code: string
  _52w_low: number
  volume: number
}

const LookUpTable = ({ marketData, newGraph = false, newMarketData }: TableProps) => {
  const { statsData } = useStore()

  const renderContent = (item: DataType, index: number) => {
    return (
      <Box key={index}>
        <Box className="flex  items-center gap-2">
          <Typography variant={item.header ? 'h4' : 'subtitle1'}>{item.value}</Typography>
          {item.prev_val && (
            <Box className=" md:flex  items-center gap-x-1 col-span-full">
              {statsData?.percentage?.toFixed(3) === 0 ? (
                <></>
              ) : (
                <LineArrowIcon
                  className={`${
                    statsData?.percentage?.toFixed(3) === 0
                      ? ''
                      : statsData?.percentage?.toFixed(3) < 0
                        ? 'rotate-90 w-3 h-3 fill-[#D32F2F]'
                        : '-rotate-90 w-3 h-3 fill-[#2E7D32]'
                  }`}
                />
              )}
              <Typography
                className="col-span-full"
                variant="badgeLabel"
                color={
                  statsData?.percentage?.toFixed(3) === 0
                    ? ''
                    : statsData?.percentage?.toFixed(3) < 0
                      ? 'error.main'
                      : 'success.main'
                }
              >
                {statsData?.change?.toFixed(3)}/{statsData?.percentage?.toFixed(3)}
              </Typography>
            </Box>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {item.label}
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      className={`w-full ${newGraph ? '' : 'rounded-[10px]'} ${newGraph ? 'bg-white' : 'bg-[#4729FF0A]'} border-red-600  grid grid-cols-1 md:flex flex-wrap items-center justify-between px-6 py-4`}
    >
      {newGraph ? (
        <Box className="w-full grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8 gap-2 pt-3">
          <Box className="flex flex-row text-center gap-6 bg-[#07004D0A] p-4 rounded-[10px] w-full">
            <Box className="flex flex-col justify-between w-full h-full">
              <Typography variant="body2">Open</Typography>
              <Typography variant="subtitle1">
                {formatDecimalValue(newMarketData?.open_price)}
              </Typography>
            </Box>
            <Box className="flex flex-col justify-between w-full h-full">
              <Typography variant="body2">Prev. Close</Typography>
              <Typography variant="subtitle1">
                {formatDecimalValue(newMarketData?.previous_close)}
              </Typography>
            </Box>
          </Box>
          <Box className="bg-[#07004D0A] text-center p-4 rounded-[10px] w-full">
            <Box className="flex flex-col justify-between w-full h-full">
              <Typography variant="body2">Volume</Typography>
              <Typography variant="subtitle1">
                {formatNumberInMillion(newMarketData?.volume)}
              </Typography>
            </Box>
          </Box>
          <Box className="flex flex-row gap-6 bg-[#07004D0A] p-4 rounded-[10px] w-full text-center">
            <Box className="flex flex-col justify-between w-full h-full">
              <Typography variant="body2">Day high</Typography>
              <Typography variant="subtitle1">{formatDecimalValue(newMarketData?.high)}</Typography>
            </Box>
            <Box className="flex flex-col justify-between w-full h-full">
              <Typography variant="body2">Day low</Typography>
              <Typography variant="subtitle1">{formatDecimalValue(newMarketData?.low)}</Typography>
            </Box>
          </Box>
          <Box className="flex flex-row gap-6 bg-[#07004D0A] p-4 rounded-[10px] w-full text-center">
            <Box className="flex flex-col justify-between w-full h-full">
              <Typography variant="body2">Year high</Typography>
              <Typography variant="subtitle1">
                {formatDecimalValue(newMarketData?._52w_high)}
              </Typography>
            </Box>
            <Box className="flex flex-col justify-between w-full h-full">
              <Typography variant="body2">Year low</Typography>
              <Typography variant="subtitle1">
                {formatDecimalValue(newMarketData?._52w_low)}
              </Typography>
            </Box>
          </Box>
          <Box className="flex flex-row gap-6 bg-[#07004D0A] p-4 rounded-[10px] w-full text-center">
            <Box className="flex flex-col justify-between w-full h-full">
              <Typography variant="body2">Bid</Typography>
              <Typography variant="subtitle1">{formatDecimalValue(newMarketData?.bid)}</Typography>
            </Box>
            <Box className="flex flex-col justify-between w-full h-full">
              <Typography variant="body2">Ask</Typography>
              <Typography variant="subtitle1">{formatDecimalValue(newMarketData?.ask)}</Typography>
            </Box>
          </Box>
          <Box className="bg-[#07004D0A] text-center p-4 rounded-[10px] w-full">
            <Box className="flex flex-col justify-between w-full h-full">
              <Typography variant="body2">Market Cap</Typography>
              <Typography variant="subtitle1">
                {marketData?.find(item => item.key === 'market_cap')?.value || 'N/A'}
              </Typography>
            </Box>
          </Box>
          <Box className="bg-[#07004D0A] text-center p-4 rounded-[10px] w-full">
            <Box className="flex flex-col justify-between w-full h-full">
              <Typography variant="body2">Shares on Issue</Typography>
              <Typography variant="subtitle1">
                {marketData?.find(item => item.key === 'shares_on_issue')?.value || 'N/A'}
              </Typography>
            </Box>
          </Box>
          <Box className="bg-[#07004D0A] text-center p-4 rounded-[10px] w-full">
            <Box className="flex flex-col justify-between w-full h-full">
              <Typography variant="body2">Daily VWAP</Typography>
              <Typography variant="subtitle1">
                {marketData?.find(item => item.key === 'vwap')?.value || 'N/A'}
              </Typography>
            </Box>
          </Box>
        </Box>
      ) : (
        marketData.map(renderContent)
      )}
    </Box>
  )
}

export default LookUpTable
